import { hierarchy, tree } from "d3-hierarchy";
import type { StaffReportingUser, StaffReportingView } from "./reportingTypes";

export const CARD_WIDTH = 216;
export const CARD_HEIGHT = 142;
const COLUMN_GAP = 36;
const ROW_GAP = 76;
const MARGIN = 32;

export type ChildSlot = { id: string; parentId: string };
export type ReportingNode = {
  id: string;
  parentId: string | null;
  user: StaffReportingUser | null;
  x: number;
  y: number;
};

export function childrenByManager(users: StaffReportingUser[], rootId?: string | null) {
  const children = new Map<string, StaffReportingUser[]>();
  for (const user of users) {
    if (user.organization_user_id === rootId || !user.reports_to_organization_user_id) continue;
    const parentId = user.reports_to_organization_user_id;
    const siblings = children.get(parentId) ?? [];
    siblings.push(user);
    children.set(parentId, siblings);
  }
  return children;
}

export function branchIds(users: StaffReportingUser[], rootId: string | null) {
  const found = new Set<string>();
  if (!rootId) return found;
  const children = childrenByManager(users, rootId);
  const queue = [rootId];
  for (let index = 0; index < queue.length; index++) {
    const id = queue[index];
    if (found.has(id)) continue;
    found.add(id);
    for (const child of children.get(id) ?? []) queue.push(child.organization_user_id);
  }
  return found;
}

export function canAssign(users: StaffReportingUser[], rootId: string | null, userId: string, parentId: string) {
  if (userId === rootId || userId === parentId) return false;
  const ids = new Set(users.map((user) => user.organization_user_id));
  return ids.has(userId) && ids.has(parentId) && !branchIds(users, userId).has(parentId);
}

export function reportingLayout(data: StaffReportingView, slots: ChildSlot[], collapsed: Set<string>, minimumWidth: number,
  order: ReadonlyMap<string, string[]> = new Map()) {
  const connected = branchIds(data.users, data.root_organization_user_id);
  const children = childrenByManager(data.users, data.root_organization_user_id);
  const nodes = new Map<string, Omit<ReportingNode, "x" | "y">>();
  for (const user of data.users) {
    if (connected.has(user.organization_user_id)) {
      nodes.set(user.organization_user_id, {
        id: user.organization_user_id, user,
        parentId: user.organization_user_id === data.root_organization_user_id ? null : user.reports_to_organization_user_id
      });
    }
  }
  const slotsByParent = new Map<string, ChildSlot[]>();
  for (const slot of slots) {
    if (!connected.has(slot.parentId)) continue;
    nodes.set(slot.id, { id: slot.id, parentId: slot.parentId, user: null });
    const list = slotsByParent.get(slot.parentId) ?? [];
    list.push(slot);
    slotsByParent.set(slot.parentId, list);
  }
  const childIds = new Map<string, string[]>();
  for (const node of nodes.values()) {
    if (!node.user) continue;
    const ids = [...(children.get(node.id) ?? []).map((user) => user.organization_user_id),
      ...(slotsByParent.get(node.id) ?? []).map((slot) => slot.id)];
    const preferred = order.get(node.id) ?? [];
    childIds.set(node.id, [...preferred.filter((id) => ids.includes(id)), ...ids.filter((id) => !preferred.includes(id))]);
  }
  const root = data.root_organization_user_id ? nodes.get(data.root_organization_user_id) : null;
  if (!root) return { nodes: [] as ReportingNode[], paths: [] as string[], width: minimumWidth, height: 0, connected, childIds };

  const graph = hierarchy(root, (node) => collapsed.has(node.id) ? [] : (childIds.get(node.id) ?? []).map((id) => nodes.get(id)!));
  const arranged = tree<Omit<ReportingNode, "x" | "y">>()
    .nodeSize([CARD_WIDTH + COLUMN_GAP, CARD_HEIGHT + ROW_GAP])(graph);
  const descendants = arranged.descendants();
  const left = Math.min(...descendants.map((node) => node.x)) - CARD_WIDTH / 2;
  const right = Math.max(...descendants.map((node) => node.x)) + CARD_WIDTH / 2;
  const width = Math.max(minimumWidth, right - left + MARGIN * 2);
  const offset = (width - (right - left)) / 2 - left;
  const positioned = descendants.map((node) => ({ ...node.data, x: node.x + offset - CARD_WIDTH / 2, y: node.y + MARGIN }));
  const positions = new Map(positioned.map((node) => [node.id, node]));
  const paths: string[] = [];
  for (const parent of descendants) {
    if (!parent.children?.length) continue;
    const position = positions.get(parent.data.id)!;
    const childrenPositions = parent.children.map((child) => positions.get(child.data.id)!);
    const parentX = position.x + CARD_WIDTH / 2;
    const parentY = position.y + CARD_HEIGHT;
    const forkY = (parentY + childrenPositions[0].y) / 2;
    const childXs = childrenPositions.map((child) => child.x + CARD_WIDTH / 2);
    // One trunk and one shared sibling bar, with a vertical branch for each child.
    paths.push(`M${parentX} ${parentY} V${forkY} M${Math.min(...childXs)} ${forkY} H${Math.max(...childXs)} `
      + childrenPositions.map((child) => `M${child.x + CARD_WIDTH / 2} ${forkY} V${child.y}`).join(" "));
  }
  return {
    nodes: positioned, paths, width,
    height: Math.max(...positioned.map((node) => node.y)) + CARD_HEIGHT + MARGIN,
    connected, childIds
  };
}
